import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Archive, Bot, Cloud, LoaderCircle, Rocket, Server } from 'lucide-react';
import { generateRandomSuffix, validateSubdomainPrefix } from '../../utils/subdomainHelper';

export default function ZipDeploy({
  onStatusMessage,
  onBack,
  api,
  beginDeploymentTracking,
  setDeploymentSessionId,
  openComposeWorkspace,
  refreshDeploymentSession,
  onTabChange
}) {
  const [appName, setAppName] = useState('');
  const [appNameError, setAppNameError] = useState('');
  const [platformSuffix] = useState(() => generateRandomSuffix());
  const [subdomainPrefix, setSubdomainPrefix] = useState('');
  const [subdomainError, setSubdomainError] = useState('');
  const [zipFile, setZipFile] = useState(null);
  const [zipDomain, setZipDomain] = useState('');
  const [zipDomainError, setZipDomainError] = useState('');
  const [internalPort, setInternalPort] = useState('3000');

  const handleSubdomainChange = (e) => {
    const val = e.target.value;
    setSubdomainPrefix(val);
    const res = validateSubdomainPrefix(val);
    setSubdomainError(res.valid ? '' : res.error);
  };

  const validateZipDomain = (val) => {
    if (!val || !val.trim()) {
      setZipDomainError('');
      return true;
    }
    if (/devorants\.com$/i.test(val.trim())) {
      setZipDomainError('devorants.com subdomains cannot be used as custom domains.');
      return false;
    }
    setZipDomainError('');
    return true;
  };

  const handleZipDomainChange = (e) => {
    const val = e.target.value;
    setZipDomain(val);
    validateZipDomain(val);
  };
  const [image, setImage] = useState('alma-node');
  const [envText, setEnvText] = useState('');
  const [deployMode, setDeployMode] = useState('static'); // static
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  const validateAppName = (val) => {
    if (!val) {
      setAppNameError('');
      return false;
    }
    if (/\s/.test(val)) {
      setAppNameError('Spaces are not allowed. Use hyphens (-) instead.');
      return false;
    }
    if (/[A-Z]/.test(val)) {
      setAppNameError('Uppercase letters are not allowed. Use lowercase only.');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(val)) {
      setAppNameError('Only lowercase letters, numbers, and hyphens (-) are allowed.');
      return false;
    }
    setAppNameError('');
    return true;
  };

  const handleAppNameChange = (e) => {
    const val = e.target.value;
    setAppName(val);
    validateAppName(val);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const parseEnvText = (rawText) => {
    const env = {};
    String(rawText || '').split('\n').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;
      const eqIdx = trimmed.indexOf('=');
      if (eqIdx > 0) {
        const k = trimmed.substring(0, eqIdx).trim();
        const v = trimmed.substring(eqIdx + 1).trim();
        if (k) env[k] = v;
      }
    });
    return env;
  };

  const buildWorkspaceInfo = (name, result = {}, session = {}) => {
    const payload = result.result || result;
    const domain = payload.domain || session.domain || zipDomain || '';
    const url = payload.url || session.url || (domain ? `http://${domain}/` : '');
    return {
      isDeploying: false,
      deploymentType: 'backend-workspace',
      basePath: '/home/app',
      autoDeploy: false,
      initialTab: 'ai',
      domain,
      url,
      port: payload.port || session.port || null,
      containerId: payload.runnerContainer || payload.containerId || payload.ssh || null,
      isSSR: Boolean(payload.isSSR),
      appReady: Boolean(payload.appReady)
    };
  };

  const pollUntilDone = (deploymentId, name) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!mountedRef.current) return;
      const session = typeof refreshDeploymentSession === 'function'
        ? await refreshDeploymentSession(deploymentId)
        : await api.getDeploymentStatus(deploymentId).catch(() => null);
      if (!session) return;

      if (session.status === 'completed') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        const result = session.result || {};
        openComposeWorkspace(name, { ...buildWorkspaceInfo(name, result, session), autoDeploy: true });
        onStatusMessage('Workspace ready — AI will install and start your app', 'success');
        setLoading(false);
      } else if (session.status === 'failed') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        const errMsg = session.error || session.message || 'Workspace setup failed';
        openComposeWorkspace(name, {
          isDeploying: false,
          deploymentType: 'backend-workspace',
          basePath: '/home/app',
          autoDeploy: false,
          initialTab: 'ai',
          setupError: errMsg
        });
        onStatusMessage(`Workspace opened with setup error: ${errMsg}`, 'error');
        setLoading(false);
      }
    }, 1500);
  };

  const handleZipDeploy = async (e) => {
    e.preventDefault();
    if (!appName || !validateAppName(appName) || !zipFile) {
      onStatusMessage(appNameError || 'Please provide app name and ZIP file', 'error');
      return;
    }

    const safeName = String(appName).trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    if (!safeName) {
      onStatusMessage('Invalid application name', 'error');
      return;
    }

    if (subdomainPrefix && !validateSubdomainPrefix(subdomainPrefix).valid) {
      onStatusMessage(subdomainError || 'Invalid subdomain format.', 'error');
      return;
    }

    if (zipDomain && !validateZipDomain(zipDomain)) {
      onStatusMessage(zipDomainError || 'devorants.com subdomains cannot be used as custom domains.', 'error');
      return;
    }

    const resolvedPrefix = (subdomainPrefix && subdomainPrefix.trim())
      ? subdomainPrefix.trim().toLowerCase()
      : safeName;
    const autoPlatformDomain = `${resolvedPrefix}-${platformSuffix}.devorants.com`;
    const finalDomain = (zipDomain && zipDomain.trim()) ? zipDomain.trim() : autoPlatformDomain;

    setLoading(true);
    setUploadPct(0);

    // Static site path (legacy): Nginx only, no AI workspace
    if (deployMode === 'static') {
      const deploymentId = beginDeploymentTracking('zip', safeName);
      setDeploymentSessionId(deploymentId);
      try {
        const response = await api.uploadApp(safeName, zipFile, {
          domain: finalDomain,
          publicPort: undefined,
          deploymentId,
          onUploadProgress: setUploadPct
        });
        if (!response.success) throw new Error(response.error || 'Upload failed');
        onStatusMessage('Static app deployed successfully', 'success');
        setAppName('');
        setSubdomainPrefix('');
        setSubdomainError('');
        setZipFile(null);
        setZipDomain('');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('switch-to-dashboard'));
          if (onTabChange) onTabChange('deployments');
        }, 1500);
      } catch (err) {
        onStatusMessage(`Error: ${err.message}`, 'error');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
      return;
    }

    // AI workspace path (same as GitHub/GitLab backend deploy)
    if (typeof openComposeWorkspace !== 'function') {
      onStatusMessage('Workspace UI is unavailable', 'error');
      setLoading(false);
      return;
    }

    const deploymentId = beginDeploymentTracking('backend-workspace', safeName);
    setDeploymentSessionId(deploymentId);

    openComposeWorkspace(safeName, {
      isDeploying: true,
      deploymentType: 'backend-workspace',
      basePath: '/home/app',
      autoDeploy: true,
      initialTab: 'ai',
      domain: finalDomain
    });

    try {
      const response = await api.deployBackendZip(safeName, zipFile, {
        domain: finalDomain,
        port: internalPort ? parseInt(internalPort, 10) : 3000,
        image,
        env: parseEnvText(envText),
        deploymentId,
        onUploadProgress: setUploadPct
      });

      const pollId = response?.deploymentId || deploymentId;
      if (response?.deploymentId && response.deploymentId !== deploymentId) {
        setDeploymentSessionId(response.deploymentId);
      }

      // If the server already finished synchronously (unlikely), open immediately.
      if (response && (response.port || response.runnerContainer || response.containerId)) {
        openComposeWorkspace(safeName, { ...buildWorkspaceInfo(safeName, response, response), autoDeploy: true });
        setLoading(false);
        return;
      }

      pollUntilDone(pollId, safeName);
    } catch (err) {
      openComposeWorkspace(safeName, {
        isDeploying: false,
        deploymentType: 'backend-workspace',
        basePath: '/home/app',
        autoDeploy: false,
        initialTab: 'ai',
        setupError: err.message || 'Automatic setup failed. Use Terminal, Files, or AI to install and run the app.'
      });
      onStatusMessage(`Error: ${err.message}`, 'error');
      setLoading(false);
    }
  };

  return (
    <div className="vercel-form-body">
      <button type="button" className="vercel-back-btn-new" onClick={onBack}>
        <ArrowLeft size={16} />
        <span>Back to selection</span>
      </button>
      <form onSubmit={handleZipDeploy} className="vercel-premium-form">
        <div className="form-section-group">
          <div className="form-section-header">
            <Bot size={18} />
            <h3>Deployment Mode</h3>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label>How should we run this ZIP?</label>
              <select value={deployMode} onChange={(e) => setDeployMode(e.target.value)}>
                <option value="static">Static site only (needs index.html, Nginx)</option>
              </select>
              <p className="form-feedback">
                Serves static files through the gateway. No container AI workspace.
              </p>
            </div>
          </div>
        </div>

        <div className="form-section-group">
          <div className="form-section-header">
            <Archive size={18} />
            <h3>General Configuration</h3>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label>Application Name <span className="required-star">*</span></label>
              <input
                type="text"
                placeholder="e.g. my-api"
                required
                value={appName}
                onChange={handleAppNameChange}
                style={appNameError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
              />
              {appNameError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {appNameError}
                </p>
              ) : (
                <p className="form-feedback">Use lowercase, numbers, and hyphens only.</p>
              )}
            </div>
            <div className="form-group">
              <label>Platform Subdomain</label>
              <div className={`platform-subdomain-group ${subdomainError ? 'has-error' : ''}`}>
                <input 
                  type="text" 
                  placeholder={appName ? appName.toLowerCase() : "e.g. my-api"} 
                  value={subdomainPrefix} 
                  onChange={handleSubdomainChange}
                />
                <span className="platform-subdomain-suffix">
                  {`-${platformSuffix}.devorants.com`}
                </span>
              </div>
              {subdomainError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {subdomainError}
                </p>
              ) : (
                <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Preview: <strong style={{ color: 'var(--primary, #6366f1)' }}>{`${(subdomainPrefix || appName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')}-${platformSuffix}.devorants.com`}</strong>
                </p>
              )}
            </div>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label>Custom Domain (optional)</label>
              <input
                type="text"
                placeholder="e.g. app.yourdomain.com"
                value={zipDomain}
                onChange={handleZipDomainChange}
                style={zipDomainError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
              />
              {zipDomainError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {zipDomainError}
                </p>
              ) : (
                <p className="form-feedback">Your own registered domain. Leave blank to use devorants.com subdomain.</p>
              )}
            </div>
          </div>

          {deployMode === 'workspace' && (
            <div className="form-row-grid">
              <div className="form-group">
                <label>App Listen Port</label>
                <input
                  type="number"
                  placeholder="3000"
                  value={internalPort}
                  onChange={(e) => setInternalPort(e.target.value)}
                />
                <p className="form-feedback">Port your app should bind to inside the container (HOST=0.0.0.0).</p>
              </div>
              <div className="form-group">
                <label>Runtime Image</label>
                <select value={image} onChange={(e) => setImage(e.target.value)}>
                  <option value="alma-node">Node.js workspace</option>
                  <option value="alma-python">Python workspace</option>
                  <option value="alma-linux">Alma Linux workspace</option>
                  <option value="node-mongo">Node + Mongo</option>
                  <option value="node-mysql">Node + MySQL</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {deployMode === 'workspace' && (
          <div className="form-section-group">
            <div className="form-section-header">
              <Server size={18} />
              <h3>Environment (optional)</h3>
            </div>
            <div className="form-group">
              <label>ENV variables</label>
              <textarea
                rows={5}
                placeholder={'KEY=value\nANOTHER_KEY=value'}
                value={envText}
                onChange={(e) => setEnvText(e.target.value)}
              />
              <p className="form-feedback">One KEY=value per line. Injected into the workspace container.</p>
            </div>
          </div>
        )}

        <div className="form-section-group">
          <div className="form-section-header">
            <Cloud size={18} />
            <h3>Project ZIP</h3>
          </div>
          <div className="form-group">
            <label>Select ZIP File <span className="required-star">*</span></label>
            <div className="premium-drop-zone" onClick={() => document.getElementById('zipInput').click()}>
              <Archive size={40} className="drop-icon" />
              <div className="drop-text">
                {zipFile ? <b>{zipFile.name}</b> : 'Click or drag your project ZIP here'}
              </div>
              <input
                id="zipInput"
                type="file"
                accept=".zip"
                onChange={(e) => setZipFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
            </div>
            {loading && uploadPct > 0 && uploadPct < 100 && (
              <p className="form-feedback">Uploading… {uploadPct}%</p>
            )}
          </div>
        </div>

        <button type="submit" disabled={loading || Boolean(appNameError) || !appName.trim() || !zipFile} className="premium-deploy-btn" style={appNameError ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
          {loading ? <LoaderCircle size={20} className="deploy-spin" /> : (deployMode === 'workspace' ? <Bot size={20} /> : <Rocket size={20} />)}
          <span>
            {loading
              ? (deployMode === 'workspace' ? 'Opening workspace…' : 'Deploying…')
              : (deployMode === 'workspace' ? 'Deploy with AI Workspace' : 'Deploy Static App')}
          </span>
        </button>
      </form>
    </div>
  );
}
